import createClient from "openapi-fetch";
import type { components, paths } from "./generated";
import type { JsonBody } from "./request-body";

export type { components, operations, paths } from "./generated";
export {
  createBuyerClient,
  createDoorPassClient,
  createPublicClient,
} from "./plane-clients";

export type ListEventsQuery = paths["/v1/events"]["get"]["parameters"]["query"];
export type ListActivitiesQuery =
  paths["/v1/activities"]["get"]["parameters"]["query"];
export type ListItemsQuery = paths["/v1/items"]["get"]["parameters"]["query"];
export type ListQuotasQuery = paths["/v1/quotas"]["get"]["parameters"]["query"];
export type ListFeeSchedulesQuery =
  paths["/v1/fee_schedules"]["get"]["parameters"]["query"];
export type ListDiscountCodesQuery =
  paths["/v1/discount_codes"]["get"]["parameters"]["query"];
export type ListCheckoutSessionsQuery =
  paths["/v1/checkout_sessions"]["get"]["parameters"]["query"];
export type ListOrdersQuery = paths["/v1/orders"]["get"]["parameters"]["query"];
export type ListPositionsQuery =
  paths["/v1/positions"]["get"]["parameters"]["query"];
export type ListEventAttendeesQuery =
  paths["/v1/events/{id}/attendees"]["get"]["parameters"]["query"];
export type ListCustomersQuery =
  paths["/v1/customers"]["get"]["parameters"]["query"];
export type ListWebhookEndpointsQuery =
  paths["/v1/webhook_endpoints"]["get"]["parameters"]["query"];
export type ListEntitlementProvidersQuery =
  paths["/v1/entitlement_providers"]["get"]["parameters"]["query"];
export type ListMembershipPlansQuery =
  paths["/v1/membership_plans"]["get"]["parameters"]["query"];
export type ListMembershipsQuery =
  paths["/v1/memberships"]["get"]["parameters"]["query"];
export type ListMembershipChargesQuery =
  paths["/v1/memberships/{id}/charges"]["get"]["parameters"]["query"];
export type ListMembershipBenefitsQuery =
  paths["/v1/membership_benefits"]["get"]["parameters"]["query"];
export type ListMembershipBenefitAssignmentsQuery =
  paths["/v1/membership_benefits/{id}/assignments"]["get"]["parameters"]["query"];
export type ListEventMembershipBenefitsQuery =
  paths["/v1/events/{id}/membership_benefits"]["get"]["parameters"]["query"];
export type ListGuestInvitationUsagesQuery =
  paths["/v1/membership_benefits/{id}/guest_invitations"]["get"]["parameters"]["query"];
export type GetGuestAllowanceQuery =
  paths["/v1/memberships/{id}/guest_allowance"]["get"]["parameters"]["query"];
export type ListInvitationPoolsQuery =
  paths["/v1/invitation_pools"]["get"]["parameters"]["query"];
export type ListInvitationAllocationsQuery =
  paths["/v1/invitation_allocations"]["get"]["parameters"]["query"];
export type ListEventQualifyingPlansQuery =
  paths["/v1/events/{id}/qualifying_plans"]["get"]["parameters"]["query"];
export type ListMembershipPlanQualifyingEventsQuery =
  paths["/v1/membership_plans/{id}/qualifying_events"]["get"]["parameters"]["query"];
export type QuoteCreate = JsonBody<paths["/v1/quotes"]["post"]>;
export type ListDoorPassesQuery =
  paths["/v1/door_passes"]["get"]["parameters"]["query"];
export type ListCheckinsQuery =
  paths["/v1/checkins"]["get"]["parameters"]["query"];
export type ListWaitlistEntriesQuery =
  paths["/v1/waitlist_entries"]["get"]["parameters"]["query"];
export type ListRefundsQuery = paths["/v1/refunds"]["get"]["parameters"]["query"];
export type ListDisputesQuery = paths["/v1/disputes"]["get"]["parameters"]["query"];
export type ListLedgerEntriesQuery = paths["/v1/ledger_entries"]["get"]["parameters"]["query"];
export type ListAttendancesQuery = paths["/v1/attendances"]["get"]["parameters"]["query"];
export type ListPositionHolderChangesQuery =
  paths["/v1/positions/{id}/holder_changes"]["get"]["parameters"]["query"];

type CreateOptions<T> = { body: JsonBody<T>; idempotencyKey: string };
/** Lets a caller bound one request, or several, with its own deadline. */
export type RequestSignal = { signal?: AbortSignal };

export type TicketingClientOptions = {
  baseUrl: string;
  organizerId: string;
  apiKey: string;
  /**
   * openapi-fetch only ever calls this with a fully-built `Request`, so the
   * narrow shape is the honest contract — and it is what lets a caller supply
   * a Cloudflare service binding's `fetch`, which is not `typeof fetch`.
   */
  fetch?: (request: Request) => Promise<Response>;
};

/**
 * The partner client is generated from ticketing-api/openapi.json. Callers
 * provide the organizer once, so tenant addressing cannot be omitted per
 * request (ADR-0010).
 */
export type TicketingClient = ReturnType<typeof partnerClient> & {
  /**
   * The same key, addressing another organizer it holds a grant for: how a
   * provisioner acts as each organizer it created. Builds a client and
   * nothing else, so make one per organizer as needed.
   */
  forOrganizer: (organizerId: string) => TicketingClient;
};

export function createTicketingClient(
  options: TicketingClientOptions,
): TicketingClient {
  return {
    ...partnerClient(options),
    forOrganizer: (organizerId) =>
      createTicketingClient({ ...options, organizerId }),
  };
}

function partnerClient(options: TicketingClientOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
    headers: {
      Authorization: `Bearer ${options.apiKey}`,
      "Ticketing-Organizer": options.organizerId,
    },
  });

  return {
    /** The organizer header is supplied by construction, never by callers. */
    listEvents: (query?: ListEventsQuery) =>
      client.GET("/v1/events", {
        // openapi-fetch's parameter type still lists this required wire header,
        // but the client-level header above is the single runtime injection.
        params: {
          ...(query ? { query } : {}),
        } as never,
      }),
    getEvent: (id: string, init?: RequestSignal) =>
      client.GET("/v1/events/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        ...init,
      }),
    createEvent: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/events"]["post"]>) =>
      client.POST("/v1/events", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            "Idempotency-Key": idempotencyKey,
          },
        },
        body,
      }),
    updateEvent: (
      id: string,
      body: JsonBody<paths["/v1/events/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/events/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    listItems: (query?: ListItemsQuery) =>
      client.GET("/v1/items", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getItem: (id: string) =>
      client.GET("/v1/items/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    createItem: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/items"]["post"]>) =>
      client.POST("/v1/items", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            "Idempotency-Key": idempotencyKey,
          },
        },
        body,
      }),
    updateItem: (
      id: string,
      body: JsonBody<paths["/v1/items/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/items/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    listQuotas: (query?: ListQuotasQuery) =>
      client.GET("/v1/quotas", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getQuota: (id: string) =>
      client.GET("/v1/quotas/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    createQuota: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/quotas"]["post"]>) =>
      client.POST("/v1/quotas", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            "Idempotency-Key": idempotencyKey,
          },
        },
        body,
      }),
    updateQuota: (
      id: string,
      body: JsonBody<paths["/v1/quotas/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/quotas/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    /**
     * Derived availability for one quota — size minus issued minus live holds
     * (ADR-0007). A DISPLAY value: the sell decision recomputes the same
     * derivation inside the per-quota transaction lock, so a page rendered
     * from this can be stale by design and must say so honestly.
     */
    getQuotaAvailability: (id: string) =>
      client.GET("/v1/quotas/{id}/availability", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    /** Live positions per item, one read for many items; ids the organizer
     *  cannot see are omitted. */
    listItemIssuance: (itemIds: ReadonlyArray<string>) =>
      client.GET("/v1/item_issuance", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          query: { item_ids: itemIds.join(",") },
        },
      }),
    /** One read for many quotas; ids the organizer cannot see are omitted. */
    listQuotaAvailability: (quotaIds: ReadonlyArray<string>) =>
      client.GET("/v1/availability", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          query: { quota_ids: quotaIds.join(",") },
        },
      }),
    listFeeSchedules: (query?: ListFeeSchedulesQuery) =>
      client.GET("/v1/fee_schedules", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getFeeSchedule: (id: string) =>
      client.GET("/v1/fee_schedules/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    // Fee schedules are read-only on the partner API (LIS-631): the platform
    // fee is Lissen's commercial term, written on the staff plane.
    listDiscountCodes: (query?: ListDiscountCodesQuery) =>
      client.GET("/v1/discount_codes", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getDiscountCode: (id: string) =>
      client.GET("/v1/discount_codes/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    createDiscountCode: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/discount_codes"]["post"]>) =>
      client.POST("/v1/discount_codes", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            "Idempotency-Key": idempotencyKey,
          },
        },
        body,
      }),
    updateDiscountCode: (
      id: string,
      body: JsonBody<paths["/v1/discount_codes/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/discount_codes/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    listMembershipPlans: (query?: ListMembershipPlansQuery) =>
      client.GET("/v1/membership_plans", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    listMembershipPlanGroups: () =>
      client.GET("/v1/membership_plan_groups", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
      }),
    getMembershipPlan: (id: string) =>
      client.GET("/v1/membership_plans/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    createMembershipPlan: ({ body, idempotencyKey }: CreateOptions<paths["/v1/membership_plans"]["post"]>) =>
      client.POST("/v1/membership_plans", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } },
        body,
      }),
    updateMembershipPlan: (id: string, input: { body: JsonBody<paths["/v1/membership_plans/{id}"]["post"]> }) =>
      client.POST("/v1/membership_plans/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
        body: input.body,
      }),
    // A paid plan's new price, for new joins and, from each one's next
    // renewal, its existing members; requires a key.
    updateMembershipPlanPrice: (
      id: string,
      { body, idempotencyKey }: CreateOptions<paths["/v1/membership_plans/{id}/price"]["post"]>,
    ) =>
      client.POST("/v1/membership_plans/{id}/price", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } },
        body,
      }),
    listMemberships: (query?: ListMembershipsQuery) =>
      client.GET("/v1/memberships", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getMembership: (id: string) =>
      client.GET("/v1/memberships/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    createMembership: ({ body, idempotencyKey }: CreateOptions<paths["/v1/memberships"]["post"]>) =>
      client.POST("/v1/memberships", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } },
        body,
      }),
    // A hosted page where a member changes the card a paid membership is
    // billed with (LIS-927); the url is single-use and short-lived.
    createMembershipPaymentMethodUpdateSession: (
      id: string,
      body: JsonBody<paths["/v1/memberships/{id}/payment_method_update_sessions"]["post"]>,
    ) =>
      client.POST("/v1/memberships/{id}/payment_method_update_sessions", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
        body,
      }),
    cancelMembership: (id: string) =>
      client.POST("/v1/memberships/{id}/cancel", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    // Pause, resume and cancellation reversal (LIS-903): each requires a key.
    pauseMembership: (id: string, idempotencyKey: string) =>
      client.POST("/v1/memberships/{id}/pause", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } },
      }),
    resumeMembership: (id: string, idempotencyKey: string) =>
      client.POST("/v1/memberships/{id}/resume", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } },
      }),
    undoMembershipCancellation: (id: string, idempotencyKey: string) =>
      client.POST("/v1/memberships/{id}/undo_cancellation", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } },
      }),
    // Move a membership's next charge to a date, or charge it now; on a
    // past_due membership, now pays the overdue invoice.
    setMembershipNextCharge: (
      id: string,
      { body, idempotencyKey }: CreateOptions<paths["/v1/memberships/{id}/next_charge"]["post"]>,
    ) =>
      client.POST("/v1/memberships/{id}/next_charge", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } },
        body,
      }),
    // A membership's settled payments and what can still be refunded (LIS-929).
    listMembershipCharges: (id: string, query?: ListMembershipChargesQuery) =>
      client.GET("/v1/memberships/{id}/charges", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id }, ...(query ? { query } : {}) },
      }),
    // Membership benefits (LIS-904): definitions, then event-side assignment.
    listMembershipBenefits: (query?: ListMembershipBenefitsQuery) =>
      client.GET("/v1/membership_benefits", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getMembershipBenefit: (id: string) =>
      client.GET("/v1/membership_benefits/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    createMembershipBenefit: ({ body, idempotencyKey }: CreateOptions<paths["/v1/membership_benefits"]["post"]>) =>
      client.POST("/v1/membership_benefits", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } },
        body,
      }),
    updateMembershipBenefit: (
      id: string,
      body: JsonBody<paths["/v1/membership_benefits/{id}"]["patch"]>,
      input: { idempotencyKey?: string } = {},
    ) =>
      client.PATCH("/v1/membership_benefits/{id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id },
        },
        body,
      }),
    /** The benefit-side usage view: every event the benefit is assigned to. */
    listMembershipBenefitAssignments: (id: string, query?: ListMembershipBenefitAssignmentsQuery) =>
      client.GET("/v1/membership_benefits/{id}/assignments", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
          ...(query ? { query } : {}),
        },
      }),
    listEventMembershipBenefits: (eventId: string, query?: ListEventMembershipBenefitsQuery) =>
      client.GET("/v1/events/{id}/membership_benefits", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: eventId },
          ...(query ? { query } : {}),
        },
      }),
    /** Create or replace a benefit's assignment to an event. Safe to retry. */
    assignEventMembershipBenefit: (
      eventId: string,
      benefitId: string,
      body: JsonBody<paths["/v1/events/{id}/membership_benefits/{benefit_id}"]["put"]>,
      input: { idempotencyKey?: string } = {},
    ) =>
      client.PUT("/v1/events/{id}/membership_benefits/{benefit_id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId, benefit_id: benefitId },
        },
        body,
      }),
    removeEventMembershipBenefit: (
      eventId: string,
      benefitId: string,
      input: { idempotencyKey?: string } = {},
    ) =>
      client.DELETE("/v1/events/{id}/membership_benefits/{benefit_id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId, benefit_id: benefitId },
        },
      }),
    /** Earned memberships (LIS-920): the plans an event counts toward. */
    listEventQualifyingPlans: (eventId: string, query?: ListEventQualifyingPlansQuery) =>
      client.GET("/v1/events/{id}/qualifying_plans", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: eventId },
          ...(query ? { query } : {}),
        },
      }),
    /** The plan-side view of the same rows: the events that count toward a plan. */
    listMembershipPlanQualifyingEvents: (planId: string, query?: ListMembershipPlanQualifyingEventsQuery) =>
      client.GET("/v1/membership_plans/{id}/qualifying_events", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: planId },
          ...(query ? { query } : {}),
        },
      }),
    /** Make an event count toward a plan's attendance rule. Safe to retry. */
    putEventQualifyingPlan: (eventId: string, planId: string, input: { idempotencyKey?: string } = {}) =>
      client.PUT("/v1/events/{id}/qualifying_plans/{plan_id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId, plan_id: planId },
        },
      }),
    removeEventQualifyingPlan: (eventId: string, planId: string, input: { idempotencyKey?: string } = {}) =>
      client.DELETE("/v1/events/{id}/qualifying_plans/{plan_id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId, plan_id: planId },
        },
      }),
    /** What cancelling an event does, configured in advance (LIS-609). */
    getEventCancellationPolicy: (eventId: string) =>
      client.GET("/v1/events/{id}/cancellation_policy", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: eventId },
        },
      }),
    /** Create or replace the policy. All five switches are required. Safe to retry. */
    putEventCancellationPolicy: (
      eventId: string,
      body: components["schemas"]["EventCancellationPolicyPut"],
      input: { idempotencyKey?: string } = {},
    ) =>
      client.PUT("/v1/events/{id}/cancellation_policy", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId },
        },
        body,
      }),
    /** Cancel an event under its policy (LIS-609). Only a reason and a confirmation. */
    cancelEvent: (
      eventId: string,
      body: components["schemas"]["EventCancel"],
      input: { idempotencyKey?: string } = {},
    ) =>
      client.POST("/v1/events/{id}/cancel", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId },
        },
        body,
      }),
    /** The cancellation, its policy snapshot and each action's progress. */
    getEventCancellation: (eventId: string) =>
      client.GET("/v1/events/{id}/cancellation", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: eventId },
        },
      }),
    /** Hand a cancellation's failed actions back to the executor. */
    retryEventCancellation: (eventId: string, input: { idempotencyKey?: string } = {}) =>
      client.POST("/v1/events/{id}/cancellation/retry", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id: eventId },
        },
      }),
    /** A customer's progress toward an earned membership plan. */
    getMembershipQualification: (planId: string, customerId: string) =>
      client.GET("/v1/membership_plans/{id}/qualification", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: planId },
          query: { customer_id: customerId },
        },
      }),
    createQuote: (body: QuoteCreate) =>
      client.POST("/v1/quotes", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    getBranding: () =>
      client.GET("/v1/branding", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
      }),
    updateBranding: (body: JsonBody<paths["/v1/branding"]["patch"]>) =>
      client.PATCH("/v1/branding", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    getNotificationProvider: () =>
      client.GET("/v1/notification_provider", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
      }),
    updateNotificationProvider: (
      body: JsonBody<paths["/v1/notification_provider"]["patch"]>,
    ) =>
      client.PATCH("/v1/notification_provider", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    getOrganizer: () =>
      client.GET("/v1/organizer", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
      }),
    /** The organizer's embed origins, replaced wholesale: where its checkout
     *  component may be framed. */
    updateOrganizer: (body: JsonBody<paths["/v1/organizer"]["patch"]>) =>
      client.PATCH("/v1/organizer", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    listActivities: (query?: ListActivitiesQuery) =>
      client.GET("/v1/activities", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getActivity: (id: string) =>
      client.GET("/v1/activities/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    /** Whether the engine sends each message an activity triggers, and where
     *  each send stands in its attempt log (ADR-0013). */
    getActivityNotifications: (id: string) =>
      client.GET("/v1/activities/{id}/notifications", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    /** Claims one message's send for the organizer's own sender, in the
     *  attempt log the engine claims in too, before the provider call. */
    claimActivityNotification: (
      id: string,
      body: components["schemas"]["NotificationSendClaimRequest"],
    ) =>
      client.POST("/v1/activities/{id}/notifications/claim", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    /** Records what became of a claimed send: sent, released or ambiguous. */
    settleActivityNotification: (
      id: string,
      body: components["schemas"]["NotificationSendSettlement"],
    ) =>
      client.POST("/v1/activities/{id}/notifications/settle", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    // ── provisioner keys (ADR-0006, 2026-10-30 amendment) ──
    /** Provisioner keys only: an organizer in the key's mode, granted to this
     *  key, collecting on the platform account. Safe to retry: a slug this
     *  key already created answers 200 with that organizer. Act as it with
     *  `forOrganizer(organizer.id)`. */
    createOrganizer: ({ body }: { body: JsonBody<paths["/v1/organizers"]["post"]> }) =>
      client.POST("/v1/organizers", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    /** Staff and provisioner keys only: where this organizer's new ticket and
     *  membership payments are charged, and its membership tax. */
    updateOrganizerCollection: ({ body }: { body: JsonBody<paths["/v1/organizer/collection"]["patch"]> }) =>
      client.PATCH("/v1/organizer/collection", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    // ── Connect (the organizer's own Stripe account) ──
    startConnectOnboarding: ({
      body,
    }: {
      body: NonNullable<paths["/v1/connect/onboarding"]["post"]["requestBody"]>["content"]["application/json"];
    }) =>
      client.POST("/v1/connect/onboarding", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    startConnectAuthorization: ({
      body,
    }: {
      body: NonNullable<paths["/v1/connect/oauth/authorization"]["post"]["requestBody"]>["content"]["application/json"];
    }) =>
      client.POST("/v1/connect/oauth/authorization", {
        params: { header: { "Ticketing-Organizer": options.organizerId } },
        body,
      }),
    // ── checkout sessions (the cart) ──
    createCheckoutSession: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/checkout_sessions"]["post"]>) =>
      client.POST("/v1/checkout_sessions", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            "Idempotency-Key": idempotencyKey,
          },
        },
        body,
      }),
    listCheckoutSessions: (query?: ListCheckoutSessionsQuery) =>
      client.GET("/v1/checkout_sessions", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getCheckoutSession: (id: string) =>
      client.GET("/v1/checkout_sessions/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    updateCheckoutSession: (
      id: string,
      body: JsonBody<paths["/v1/checkout_sessions/{id}"]["patch"]>,
    ) =>
      client.PATCH("/v1/checkout_sessions/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
        body,
      }),
    expireCheckoutSession: (id: string) =>
      client.POST("/v1/checkout_sessions/{id}/expire", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    // ── what a settled session becomes (reads, plus the customer's contact) ──
    listOrders: (query?: ListOrdersQuery) =>
      client.GET("/v1/orders", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getOrder: (id: string) =>
      client.GET("/v1/orders/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    listPositions: (query?: ListPositionsQuery) =>
      client.GET("/v1/positions", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    listEventAttendees: (eventId: string, query?: ListEventAttendeesQuery) =>
      client.GET("/v1/events/{id}/attendees", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id: eventId },
          ...(query ? { query } : {}),
        },
      }),
    getPosition: (id: string) =>
      client.GET("/v1/positions/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    listCustomers: (query?: ListCustomersQuery) =>
      client.GET("/v1/customers", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          ...(query ? { query } : {}),
        },
      }),
    getCustomer: (id: string) =>
      client.GET("/v1/customers/{id}", {
        params: {
          header: { "Ticketing-Organizer": options.organizerId },
          path: { id },
        },
      }),
    // The customer's current name and email (LIS-930). Orders keep their
    // buyer snapshot; `billing_contact_sync` reports the Stripe side.
    updateCustomer: (
      id: string,
      body: JsonBody<paths["/v1/customers/{id}"]["patch"]>,
      idempotencyKey?: string,
    ) =>
      client.PATCH("/v1/customers/{id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
          },
          path: { id },
        },
        body,
      }),
    /** Ticket transfers (LIS-909): the current holder, attested by you, gives
     *  one position to another customer. Same position, QR and money. */
    transferPosition: (id: string, { body, idempotencyKey }: CreateOptions<paths["/v1/positions/{id}/transfer"]["post"]>) =>
      client.POST("/v1/positions/{id}/transfer", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } }, body }),
    /** The organizer's audited holder override, with a required reason;
     *  allowed after admission and never touches check-ins. */
    correctPositionHolder: (id: string, { body, idempotencyKey }: CreateOptions<paths["/v1/positions/{id}/holder_correction"]["post"]>) =>
      client.POST("/v1/positions/{id}/holder_correction", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } }, body }),
    listPositionHolderChanges: (id: string, query?: ListPositionHolderChangesQuery) =>
      client.GET("/v1/positions/{id}/holder_changes", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id }, ...(query ? { query } : {}) } }),
    /** A recipient you signed in and whose address you verified claims the
     *  tickets sent to that address. API key only. */
    claimPositions: ({ body, idempotencyKey }: CreateOptions<paths["/v1/position_claims"]["post"]>) =>
      client.POST("/v1/position_claims", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    /** Attendance credited at check-in (LIS-919): one visit per customer and
     *  event, derived from admitted, non-voided check-ins. */
    listAttendances: (query: ListAttendancesQuery) =>
      client.GET("/v1/attendances", { params: { header: { "Ticketing-Organizer": options.organizerId }, query } }),
    /** The organizer's audited correction of who an admission credits. */
    correctCheckinCredit: (id: string, { body, idempotencyKey }: CreateOptions<paths["/v1/checkins/{id}/credit_correction"]["post"]>) =>
      client.POST("/v1/checkins/{id}/credit_correction", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } }, body }),
    /** Complimentary tickets (LIS-827): one idempotent call issues `quantity`
     *  free positions of an item to a recipient as an ordinary zero-total order. */
    // Invitation pools and allocations (LIS-911): organizer-owned budgets a
    // comp can be issued against. The engine knows no actor behind either.
    listInvitationPools: (query?: ListInvitationPoolsQuery) =>
      client.GET("/v1/invitation_pools", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getInvitationPool: (id: string) =>
      client.GET("/v1/invitation_pools/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    createInvitationPool: ({ body, idempotencyKey }: CreateOptions<paths["/v1/invitation_pools"]["post"]>) =>
      client.POST("/v1/invitation_pools", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } },
        body,
      }),
    updateInvitationPool: (
      id: string,
      body: JsonBody<paths["/v1/invitation_pools/{id}"]["patch"]>,
      input: { idempotencyKey?: string } = {},
    ) =>
      client.PATCH("/v1/invitation_pools/{id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id },
        },
        body,
      }),
    listInvitationAllocations: (query?: ListInvitationAllocationsQuery) =>
      client.GET("/v1/invitation_allocations", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getInvitationAllocation: (id: string) =>
      client.GET("/v1/invitation_allocations/{id}", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } },
      }),
    createInvitationAllocation: ({
      body,
      idempotencyKey,
    }: CreateOptions<paths["/v1/invitation_allocations"]["post"]>) =>
      client.POST("/v1/invitation_allocations", {
        params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } },
        body,
      }),
    updateInvitationAllocation: (
      id: string,
      body: JsonBody<paths["/v1/invitation_allocations/{id}"]["patch"]>,
      input: { idempotencyKey?: string } = {},
    ) =>
      client.PATCH("/v1/invitation_allocations/{id}", {
        params: {
          header: {
            "Ticketing-Organizer": options.organizerId,
            ...(input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : {}),
          },
          path: { id },
        },
        body,
      }),
    /** Invite a guest with a member's allowance (LIS-910). Idempotency-Key required. */
    createGuestInvitation: ({ body, idempotencyKey }: CreateOptions<paths["/v1/guest_invitations"]["post"]>) =>
      client.POST("/v1/guest_invitations", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    /** A member's limit, usage and remaining units for one guest benefit. */
    getGuestAllowance: (membershipId: string, query: GetGuestAllowanceQuery) =>
      client.GET("/v1/memberships/{id}/guest_allowance", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id: membershipId }, query },
      }),
    /** The invitations made under a guest benefit, newest first. */
    listGuestInvitationUsages: (benefitId: string, query?: ListGuestInvitationUsagesQuery) =>
      client.GET("/v1/membership_benefits/{id}/guest_invitations", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id: benefitId }, ...(query ? { query } : {}) },
      }),
    createComp: ({ body, idempotencyKey }: CreateOptions<paths["/v1/comps"]["post"]>) =>
      client.POST("/v1/comps", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    createRefund: ({ body, idempotencyKey }: CreateOptions<paths["/v1/refunds"]["post"]>) =>
      client.POST("/v1/refunds", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    listRefunds: (query?: ListRefundsQuery) =>
      client.GET("/v1/refunds", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    getRefund: (id: string) =>
      client.GET("/v1/refunds/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    listDisputes: (query?: ListDisputesQuery) =>
      client.GET("/v1/disputes", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    getDispute: (id: string) =>
      client.GET("/v1/disputes/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    listLedgerEntries: (query?: ListLedgerEntriesQuery) =>
      client.GET("/v1/ledger_entries", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    listWebhookEndpoints: (query?: ListWebhookEndpointsQuery) =>
      client.GET("/v1/webhook_endpoints", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getWebhookEndpoint: (id: string) =>
      client.GET("/v1/webhook_endpoints/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    createWebhookEndpoint: ({ body, idempotencyKey }: CreateOptions<paths["/v1/webhook_endpoints"]["post"]>) =>
      client.POST("/v1/webhook_endpoints", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    updateWebhookEndpoint: (id: string, body: JsonBody<paths["/v1/webhook_endpoints/{id}"]["patch"]>, idempotencyKey?: string) =>
      client.PATCH("/v1/webhook_endpoints/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } }, body }),
    deleteWebhookEndpoint: (id: string, idempotencyKey?: string) =>
      client.DELETE("/v1/webhook_endpoints/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } } }),
    rotateWebhookEndpoint: (id: string, idempotencyKey?: string) =>
      client.POST("/v1/webhook_endpoints/{id}/rotate", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } } }),
    // ── entitlement providers ──
    listEntitlementProviders: (query?: ListEntitlementProvidersQuery) =>
      client.GET("/v1/entitlement_providers", {
        params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) },
      }),
    getEntitlementProvider: (id: string) =>
      client.GET("/v1/entitlement_providers/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    createEntitlementProvider: ({ body, idempotencyKey }: CreateOptions<paths["/v1/entitlement_providers"]["post"]>) =>
      client.POST("/v1/entitlement_providers", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    updateEntitlementProvider: (id: string, body: JsonBody<paths["/v1/entitlement_providers/{id}"]["patch"]>, idempotencyKey?: string) =>
      client.PATCH("/v1/entitlement_providers/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } }, body }),
    // ── door passes ──
    createDoorPass: (
      body: JsonBody<paths["/v1/door_passes"]["post"]>,
      init?: RequestSignal & { idempotencyKey?: string },
    ) =>
      client.POST("/v1/door_passes", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(init?.idempotencyKey ? { "Idempotency-Key": init.idempotencyKey } : {}) } }, body, signal: init?.signal }),
    listDoorPasses: (query?: ListDoorPassesQuery) =>
      client.GET("/v1/door_passes", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    getDoorPass: (id: string) =>
      client.GET("/v1/door_passes/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    revokeDoorPass: (id: string) =>
      client.POST("/v1/door_passes/{id}/revoke", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    listCheckins: (query?: ListCheckinsQuery) =>
      client.GET("/v1/checkins", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    getCheckin: (id: string) =>
      client.GET("/v1/checkins/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    voidCheckin: (id: string) =>
      client.POST("/v1/checkins/{id}/void", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    // ── item-scoped waitlists ──
    createWaitlistEntry: ({ body, idempotencyKey }: CreateOptions<paths["/v1/waitlist_entries"]["post"]>) =>
      client.POST("/v1/waitlist_entries", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey } }, body }),
    listWaitlistEntries: (query?: ListWaitlistEntriesQuery) =>
      client.GET("/v1/waitlist_entries", { params: { header: { "Ticketing-Organizer": options.organizerId }, ...(query ? { query } : {}) } }),
    getWaitlistEntry: (id: string) =>
      client.GET("/v1/waitlist_entries/{id}", { params: { header: { "Ticketing-Organizer": options.organizerId }, path: { id } } }),
    removeWaitlistEntry: (id: string, idempotencyKey?: string) =>
      client.POST("/v1/waitlist_entries/{id}/remove", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } } }),
    rejectWaitlistEntry: (id: string, idempotencyKey?: string) =>
      client.POST("/v1/waitlist_entries/{id}/reject", { params: { header: { "Ticketing-Organizer": options.organizerId, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, path: { id } } }),
    promoteWaitlistEntry: (id: string, idempotencyKey: string) =>
      client.POST("/v1/waitlist_entries/{id}/promote", { params: { header: { "Ticketing-Organizer": options.organizerId, "Idempotency-Key": idempotencyKey }, path: { id } } }),
  };
}
